// Requests — the team's own asks, filed from Slack.
//
// A staffer mentions @genius in Slack with an idea, a bug or a build request;
// squish-crm-backend turns the mention into a card (GET /api/requests). This
// page is where a card gets picked up, worked (internal notes) and closed with
// a note that goes back to whoever asked.
//
// NOT TICKETS. Tickets are customers' Salesforce cases, and that page's words
// (case, reply to the customer) are deliberately absent here: a request is
// internal and its poster is a colleague. The one thing on this page that
// leaves the CRM is the closing note — posted to the Slack thread and DMed to
// the poster — and the box it is typed into says so.
//
// Everything on a card was typed into Slack by somebody. It is rendered as
// React text children throughout — no HTML, no markdown — and the permalink
// only becomes a link once lib/requests.js slackPermalink() has judged it.

import React, { useEffect, useState, useCallback, useRef, useId } from 'react';
import { fetchRequests, claimRequest, releaseRequest, addRequestNote, closeRequest, reopenRequest } from '../api';
import { useViewContext } from '../lib/viewContext';
import {
  OUTCOMES, CLOSE_NOTE_MAX, outcomeLabel, isMine, groupRequests, upsertRequest, standingOffer,
  slackPermalink, channelLabel, relativeAge, threadTime, closeNoteProblem, deliveryLines, isStale, initials, requestImages,
} from '../lib/requests';
import { RequestImageThumb, RequestImageLightbox } from '../components/RequestImages';

// The list is re-read this often while the page is mounted and the tab is in
// front. App.jsx polls the same endpoint once a minute for the nav badge.
const POLL_MS = 30_000;

function Loading() {
  return <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>;
}

function formatDateTime(ms) {
  const d = ms ? new Date(ms) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatTime(ms) {
  const d = ms ? new Date(ms) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const without = (obj, key) => {
  const { [key]: _dropped, ...rest } = obj;
  return rest;
};

const STATUS_LABEL = { open: 'Unassigned', claimed: 'In progress', closed: 'Closed' };
const STATUS_BADGE = { open: 'badge-yellow', claimed: 'badge-blue', closed: 'badge-gray' };
const OUTCOME_BADGE = { done: 'badge-green', wont_do: 'badge-gray', duplicate: 'badge-gray' };

const NOTE_PLACEHOLDER = {
  done: 'What you did…',
  wont_do: 'Why this is not being done…',
  duplicate: 'Which request this duplicates…',
};

const ownerName = (owner) => owner?.name || owner?.email || 'Someone else';

// ─── Take over ────────────────────────────────────────────────────────────────
// Somebody else has the card. Reached two ways: a Pick up that lost the race
// (the server answered 409 already_claimed and named them), or Take over on a
// card already listed as theirs. Either way it is asked once, here, before the
// claim is re-sent with force.
function TakeoverOffer({ offer, busy, onConfirm, onCancel }) {
  const who = ownerName(offer.owner);
  return (
    <div className="req-takeover" role={offer.raced ? 'alert' : undefined}>
      <span>
        {offer.raced ? `${who} picked this up before you did.` : `${who} has this one.`} Taking it over moves it to you.
      </span>
      <button data-write type="button" className="req-btn req-btn-primary" onClick={onConfirm} disabled={Boolean(busy)}>
        {busy === 'claim' ? 'Taking over…' : 'Take over'}
      </button>
      <button type="button" className="req-btn" onClick={onCancel} disabled={Boolean(busy)}>Leave it</button>
    </div>
  );
}

// ─── One card ─────────────────────────────────────────────────────────────────
// A request is a thing someone picks up, so it is drawn as one: a small card
// with the ask as its headline. The whole card opens the request — the open
// button's hit area is stretched over it — and Pick up sits on top of that, a
// sibling rather than a child, because a button inside a button is not a
// button to a keyboard or a screen reader.
//
// Colour says where a card stands and nothing else: yellow is waiting on
// somebody, the accent is yours, grey is someone else's, green is done. The
// age turns yellow too once an unassigned card has waited past two days.
const LANE_TONE = { open: 'is-open', mine: 'is-mine', theirs: 'is-theirs', closed: 'is-closed' };

function PersonChip({ name, mine, closedBy = false }) {
  const said = closedBy ? `Closed by ${name}` : mine ? 'You have this one' : `${name} has this one`;
  return (
    <span className={`req-chip${mine ? ' is-mine' : ''}`} title={said}>
      <span aria-hidden="true">{mine ? 'You' : initials(name)}</span>
      <span className="req-sr">{said}</span>
    </span>
  );
}

function RequestCard({ r, me, selected, busy, error, offer, actions, onOpen }) {
  const closed = r.status === 'closed';
  const unassigned = r.status === 'open';
  const mine = !closed && !unassigned && isMine(r, me);
  const tone = closed ? 'closed' : unassigned ? 'open' : mine ? 'mine' : 'theirs';
  const channel = channelLabel(r.slack);
  const noteCount = Array.isArray(r.notes) ? r.notes.filter(n => !n?.system).length : 0;
  const imageCount = requestImages(r).length;
  const stale = isStale(r);
  const when = closed ? (r.closedAt ?? r.resolution?.at) : r.createdAt;
  return (
    <li className={`req-card ${LANE_TONE[tone]}${selected ? ' is-selected' : ''}`}>
      <div className="req-card-top">
        <span className="req-ref">{r.ref || '—'}</span>
        {closed && (
          <span className={`badge ${OUTCOME_BADGE[r.resolution?.outcome] || 'badge-gray'}`}>{outcomeLabel(r.resolution?.outcome)}</span>
        )}
        <span className={`req-card-age${stale ? ' is-stale' : ''}`} title={`${closed ? 'Closed' : 'Filed'} ${formatDateTime(when)}`}>
          {relativeAge(when)}
        </span>
      </div>
      <button type="button" className="req-card-open" onClick={() => onOpen(r.id)} aria-haspopup="dialog">
        {r.title || r.text || '—'}
      </button>
      <div className="req-card-foot">
        <span className="req-card-meta">
          {r.poster?.name || 'Unknown poster'}{channel && <> · {channel}</>}
          {imageCount > 0 && <> · {imageCount} image{imageCount === 1 ? '' : 's'}</>}
          {noteCount > 0 && <> · {noteCount} note{noteCount === 1 ? '' : 's'}</>}
        </span>
        {unassigned && (
          <button data-write type="button" className="req-btn req-btn-primary req-card-action" onClick={() => actions.pickUp(r)} disabled={Boolean(busy)}>
            {busy === 'claim' ? 'Picking up…' : 'Pick up'}
          </button>
        )}
        {!closed && !unassigned && <PersonChip name={ownerName(r.owner)} mine={mine} />}
        {closed && r.resolution?.by && <PersonChip name={r.resolution.by.name || r.resolution.by.email || 'Someone'} mine={false} closedBy />}
      </div>
      {offer && (
        <div className="req-card-under">
          <TakeoverOffer offer={offer} busy={busy} onConfirm={() => actions.takeOver(r)} onCancel={actions.dismissTakeover} />
        </div>
      )}
      {error?.action === 'claim' && <div className="req-card-under req-inline-error" role="alert">⚠ {error.message}</div>}
    </li>
  );
}

// One column of the board. The count is said once, in the lane's own colour,
// and an empty lane keeps its place so the board does not jump around as
// cards move between lanes.
function Lane({ title, tone, list, empty, renderCards }) {
  return (
    <section className={`req-lane ${LANE_TONE[tone]}`} aria-label={title}>
      <h2 className="req-lane-head">
        <span>{title}</span>
        <span className="req-lane-count">{list.length}</span>
      </h2>
      {list.length > 0 ? renderCards(list) : <div className="req-lane-empty">{empty}</div>}
    </section>
  );
}

// ─── Request detail ───────────────────────────────────────────────────────────
// A drawer from the right, like the case workspace on Tickets, and narrower:
// there is one column of things to read here, not a thread beside a rail.
//
// Two boxes take typing and they go to different places. The internal note
// stays in the CRM. The closing note is posted to Slack. Each says which it is
// in its own label, and the closing one says it again underneath.
function RequestDetail({ r, me, busy, error, offer, draft, onDraft, actions, onClose }) {
  const panelRef = useRef(null);
  const noteId = useId();
  const outcomeId = useId();
  const closeNoteId = useId();
  const closeHelpId = useId();

  const closed = r.status === 'closed';
  const unassigned = r.status === 'open';
  const mine = !closed && !unassigned && isMine(r, me);
  const theirs = !closed && !unassigned && !mine;
  const link = slackPermalink(r.slack?.permalink);
  const channel = channelLabel(r.slack);
  const thread = Array.isArray(r.thread) ? r.thread : [];
  const notes = Array.isArray(r.notes) ? r.notes : [];
  const images = requestImages(r);
  const [openImage, setOpenImage] = useState(null);

  const note = draft.note || '';
  const closeNote = draft.closeNote || '';
  const outcome = draft.outcome || OUTCOMES[0].value;
  const closeProblem = closeNoteProblem(closeNote);
  const closeLength = closeNote.trim().length;
  const overLimit = closeLength > CLOSE_NOTE_MAX;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Focus goes into the panel when it opens and back to the row it was opened
  // from when it closes, so Escape leaves the keyboard where it was.
  useEffect(() => {
    const opener = document.activeElement;
    panelRef.current?.focus();
    return () => { if (opener instanceof HTMLElement && document.contains(opener)) opener.focus(); };
  }, []);

  // The open request is the most specific thing on screen — level 2 so it sits
  // above the request list in the ~ console's breadcrumb.
  //
  // Only what this app made up goes in: the reference, the status, counts. The
  // title, the poster's name and the notes are text somebody typed into Slack,
  // and the view context is read by the ~ console's model, which can act on
  // customer accounts. A request card must not be a way to talk to it.
  useViewContext({
    label: `Request ${r.ref || ''}`.trim(),
    view: 'request-detail',
    requestId: r.id,
    requestRef: r.ref || null,
    requestStatus: r.status || null,
    requestNoteCount: notes.length,
    requestOutcome: r.resolution?.outcome || null,
  }, 2);

  const submitNote = (e) => {
    e.preventDefault();
    if (!note.trim() || busy) return;
    actions.addNote(r, note.trim());
  };

  const submitClose = (e) => {
    e.preventDefault();
    if (closeProblem || busy) return;
    actions.close(r, { outcome, note: closeNote.trim() });
  };

  const sectionLabel = { fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 };
  const card = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' };
  // Slack text, shown as typed: line breaks kept, nothing interpreted.
  const asTyped = { fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
  const meta = { fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' };
  const actionRow = { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Request ${r.ref || ''}`.trim()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(720px, 100vw)',
          background: 'var(--bg)', borderLeft: '1px solid var(--border)',
          zIndex: 101, display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.4)', outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 24px 14px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className="req-ref" style={{ marginTop: 0 }}>{r.ref || '—'}</span>
              <span className={`badge ${STATUS_BADGE[r.status] || 'badge-blue'}`}>{STATUS_LABEL[r.status] || 'In progress'}</span>
              {closed && r.resolution && (
                <span className={`badge ${OUTCOME_BADGE[r.resolution.outcome] || 'badge-gray'}`}>{outcomeLabel(r.resolution.outcome)}</span>
              )}
              <span style={meta}>Internal request</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35, marginTop: 6, overflowWrap: 'anywhere' }}>
              {r.title || '—'}
            </div>
            <div style={{ ...meta, marginTop: 4 }}>
              From {r.poster?.name || 'an unknown poster'}{channel ? ` in ${channel}` : ''} · {formatDateTime(r.createdAt)} · {relativeAge(r.createdAt)}
            </div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              {link
                ? <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Open in Slack ↗</a>
                : <span style={{ color: 'var(--text3)' }}>No Slack link for this one.</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close panel" title="Close (Esc) — anything you have typed is kept" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 24px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* The ask */}
          <section>
            <div style={sectionLabel}>The ask</div>
            <div style={{ ...card, ...asTyped }}>{r.text || r.title || '—'}</div>
            {images.length > 0 && (
              <div className="req-images">
                {images.map(img => <RequestImageThumb key={img.id} requestId={r.id} image={img} onOpen={setOpenImage} />)}
              </div>
            )}
          </section>

          {thread.length > 0 && (
            <section>
              <div style={sectionLabel}>Slack thread context · earlier messages, for background</div>
              <ul className="req-thread">
                {thread.map((m, i) => {
                  const at = threadTime(m?.ts);
                  return (
                    <li key={`${m?.ts || ''}-${i}`}>
                      <b>{m?.userName || 'Someone'}</b>
                      {at && <time dateTime={new Date(at).toISOString()}>{formatDateTime(at)}</time>}
                      {'\n'}{m?.text || ''}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Resolution — what was done, and what Slack actually got */}
          {closed && (
            <section>
              <div style={sectionLabel}>Resolution</div>
              {r.resolution ? (
                <div style={card}>
                  <div style={{ ...actionRow, marginBottom: 8 }}>
                    <span className={`badge ${OUTCOME_BADGE[r.resolution.outcome] || 'badge-gray'}`}>{outcomeLabel(r.resolution.outcome)}</span>
                    <span style={meta}>
                      Closed by {r.resolution.by?.name || r.resolution.by?.email || 'someone'} · {formatDateTime(r.resolution.at || r.closedAt)}
                    </span>
                  </div>
                  <div style={asTyped}>{r.resolution.note || '—'}</div>
                  <ul className="req-delivery">
                    {deliveryLines(r.resolution, r.poster?.name).map(l => (
                      <li key={l.text} className={l.tone === 'ok' ? 'is-ok' : 'is-warn'}>{l.tone === 'ok' ? '✓' : '⚠'} {l.text}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Closed, but no resolution was recorded.</div>
              )}
              <div data-write style={{ ...actionRow, marginTop: 12 }}>
                <button type="button" className="req-btn" onClick={() => actions.reopen(r)} disabled={Boolean(busy)}>
                  {busy === 'reopen' ? 'Reopening…' : 'Reopen'}
                </button>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Reopening puts it back in progress, assigned to you.</span>
              </div>
              {error?.action === 'reopen' && <div className="req-inline-error" role="alert" style={{ marginTop: 8 }}>⚠ {error.message}</div>}
            </section>
          )}

          {/* Owner */}
          {!closed && (
            <section>
              <div style={sectionLabel}>Owner</div>
              <div style={actionRow}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>
                  {unassigned ? 'Nobody has picked this up yet.' : mine ? 'You have this one.' : `${ownerName(r.owner)} has this one.`}
                </span>
                {!unassigned && r.claimedAt && <span style={meta}>since {formatDateTime(r.claimedAt)}</span>}
                {unassigned && (
                  <button data-write type="button" className="req-btn req-btn-primary" onClick={() => actions.pickUp(r)} disabled={Boolean(busy)}>
                    {busy === 'claim' ? 'Picking up…' : 'Pick up'}
                  </button>
                )}
                {mine && (
                  <button data-write type="button" className="req-btn" onClick={() => actions.release(r)} disabled={Boolean(busy)} title="Back to Unassigned, for someone else to pick up">
                    {busy === 'release' ? 'Releasing…' : 'Release'}
                  </button>
                )}
                {theirs && !offer && (
                  <button data-write type="button" className="req-btn" onClick={() => actions.askTakeover(r)} disabled={Boolean(busy)}>Take over</button>
                )}
              </div>
              {offer && (
                <div style={{ marginTop: 10 }}>
                  <TakeoverOffer offer={offer} busy={busy} onConfirm={() => actions.takeOver(r)} onCancel={actions.dismissTakeover} />
                </div>
              )}
              {(error?.action === 'claim' || error?.action === 'release') && (
                <div className="req-inline-error" role="alert" style={{ marginTop: 8 }}>⚠ {error.message}</div>
              )}
            </section>
          )}

          {/* Internal notes — these never leave the CRM */}
          <section>
            <div style={sectionLabel}>Internal notes ({notes.length}) · staff only, never sent to Slack</div>
            {notes.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No notes yet.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {notes.map((n, i) => (
                <div key={`${n?.at || ''}-${i}`} style={{ ...card, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{n?.by?.name || n?.by?.email || 'Unknown'}</span>
                    <span style={meta}>{formatDateTime(n?.at)}</span>
                  </div>
                  <div style={asTyped}>{n?.text || ''}</div>
                </div>
              ))}
            </div>
            <form data-write onSubmit={submitNote} style={{ marginTop: 12 }}>
              <label className="req-label" htmlFor={noteId}>Add an internal note <span>(not sent to Slack)</span></label>
              <textarea
                id={noteId}
                className="req-input"
                rows={3}
                value={note}
                onChange={e => onDraft({ note: e.target.value })}
                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitNote(e); }}
                placeholder="What you found, what you tried, what is left…"
              />
              <div style={{ ...actionRow, marginTop: 8 }}>
                <button type="submit" className="req-btn" disabled={!note.trim() || Boolean(busy)}>
                  {busy === 'note' ? 'Adding…' : 'Add note'}
                </button>
                {error?.action === 'note' && <span className="req-inline-error" role="alert">⚠ {error.message}</span>}
              </div>
            </form>
          </section>

          {/* Close — the note in this box is the one thing here that goes to Slack */}
          {!closed && (
            <form data-write onSubmit={submitClose}>
              <div style={sectionLabel}>Close this request</div>
              {theirs && (
                <div style={{ fontSize: 12, color: 'var(--badge-yellow)', marginBottom: 10 }}>
                  ⚠ {ownerName(r.owner)} has this one. Closing it here closes it for them too.
                </div>
              )}
              <label className="req-label" htmlFor={outcomeId}>Outcome</label>
              <select id={outcomeId} className="req-input" value={outcome} onChange={e => onDraft({ outcome: e.target.value })}>
                {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label className="req-label" htmlFor={closeNoteId} style={{ marginTop: 12 }}>Closing note <span>(required · sent to Slack)</span></label>
              <textarea
                id={closeNoteId}
                className="req-input"
                rows={4}
                required
                aria-describedby={closeHelpId}
                value={closeNote}
                onChange={e => onDraft({ closeNote: e.target.value })}
                placeholder={NOTE_PLACEHOLDER[outcome] || NOTE_PLACEHOLDER.done}
              />
              <p id={closeHelpId} className="req-outbound">
                <strong>This note is posted to the Slack thread and DMed to the poster.</strong> Keep customer details out of it: mention customers by account number only.
              </p>
              <div style={{ ...actionRow, marginTop: 10 }}>
                <button type="submit" className="req-btn req-btn-primary" disabled={Boolean(closeProblem) || Boolean(busy)}>
                  {busy === 'close' ? 'Closing…' : `Close as ${outcomeLabel(outcome)}`}
                </button>
                <span style={{ ...meta, color: overLimit ? 'var(--accent2)' : closeLength > CLOSE_NOTE_MAX * 0.9 ? 'var(--badge-yellow)' : 'var(--text3)' }}>
                  {closeLength.toLocaleString()} / {CLOSE_NOTE_MAX.toLocaleString()}{overLimit ? ' — too long' : ''}
                </span>
              </div>
              {error?.action === 'close' && <div className="req-inline-error" role="alert" style={{ marginTop: 8 }}>⚠ {error.message}</div>}
            </form>
          )}
        </div>
      </div>
      {openImage && <RequestImageLightbox requestId={r.id} image={openImage} onClose={() => setOpenImage(null)} />}
    </>
  );
}

// ─── Requests list ────────────────────────────────────────────────────────────

export default function Requests({ me: staff, onCounts }) {
  // null until a load has ANSWERED. A failed load leaves it null, so "Nothing
  // waiting" below can only ever be said about a list that actually came back.
  const [data, setData]               = useState(null);
  const [loadError, setLoadError]     = useState('');
  const [loadedAt, setLoadedAt]       = useState(null);
  const [refreshing, setRefreshing]   = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [showClosed, setShowClosed]   = useState(false);
  // Per card, so a slow pick-up on one row does not freeze the others.
  const [busy, setBusy]               = useState({});   // { [id]: 'claim' | 'release' | 'note' | 'close' | 'reopen' }
  const [actionError, setActionError] = useState({});   // { [id]: { action, message } }
  const [offer, setOffer]             = useState(null); // { id, owner, raced } — someone else has it; Take over?
  // What has been typed into a card's two boxes. Held here, not in the panel,
  // so Escape or a stray click on the backdrop does not throw a note away.
  const [drafts, setDrafts]           = useState({});   // { [id]: { note, closeNote, outcome } }

  // Every list read takes a number; an answer is only used if nothing has
  // superseded it. A mutation takes one too, so a poll that was already in
  // flight cannot land afterwards and put the card back the way it was.
  const seqRef = useRef(0);
  const inFlight = useRef(new Set());
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;
  const staffRef = useRef(staff);
  staffRef.current = staff;

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setRefreshing(true);
    try {
      const body = await fetchRequests();
      if (seq !== seqRef.current) return;
      // A 200 without a list is not an empty list.
      if (!Array.isArray(body?.requests)) throw new Error('The request list came back in a shape this page cannot read');
      setData(body);
      setLoadError('');
      setLoadedAt(Date.now());
      // A Take over offer is only good while someone else still has the card.
      setOffer(o => standingOffer(o, body.requests, body.me?.email ? body.me : staffRef.current));
      onCountsRef.current?.(body.counts);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setLoadError(e.message || 'Could not load requests');
    } finally {
      if (seq === seqRef.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { seqRef.current++; };
  }, [load]);

  // Poll while the page is up and the tab is in front. A hidden tab stops
  // asking, and asks once as soon as it is looked at again.
  useEffect(() => {
    const tick = () => { if (!document.hidden) load(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [load]);

  // One mutation per card at a time. The card the server hands back replaces
  // the one on screen straight away; the list is then re-read for the counts
  // and for whatever else moved meanwhile — after a failure too, since a
  // refusal usually means the list was out of date.
  const act = useCallback(async (id, action, call) => {
    if (inFlight.current.has(id)) return false;
    inFlight.current.add(id);
    setBusy(b => ({ ...b, [id]: action }));
    setActionError(errs => without(errs, id));
    let ok = false;
    try {
      const request = await call();
      seqRef.current++;
      if (request?.id) setData(d => (d ? { ...d, requests: upsertRequest(d.requests, request) } : d));
      setOffer(o => (o?.id === id ? null : o));
      ok = true;
    } catch (e) {
      if (action === 'claim' && e.status === 409 && e.code === 'already_claimed') {
        // The refusal says who has it. Show that now, rather than leaving a
        // Pick up button on a card we have just been told is taken.
        seqRef.current++;
        if (e.owner) setData(d => (d ? { ...d, requests: d.requests.map(r => (r?.id === id ? { ...r, status: 'claimed', owner: e.owner } : r)) } : d));
        setOffer({ id, owner: e.owner || null, raced: true });
      } else {
        setActionError(errs => ({ ...errs, [id]: { action, message: e.message || 'That did not go through' } }));
      }
    }
    inFlight.current.delete(id);
    setBusy(b => without(b, id));
    load();
    return ok;
  }, [load]);

  const setDraft = useCallback((id, patch) => {
    setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));
  }, []);

  const actions = {
    pickUp:   (r) => act(r.id, 'claim', () => claimRequest(r.id)),
    takeOver: (r) => act(r.id, 'claim', () => claimRequest(r.id, { force: true })),
    release:  (r) => act(r.id, 'release', () => releaseRequest(r.id)),
    reopen:   (r) => act(r.id, 'reopen', () => reopenRequest(r.id)),
    askTakeover: (r) => setOffer({ id: r.id, owner: r.owner, raced: false }),
    dismissTakeover: () => setOffer(null),
    addNote: async (r, text) => {
      if (await act(r.id, 'note', () => addRequestNote(r.id, text))) setDraft(r.id, { note: '' });
    },
    close: async (r, { outcome, note }) => {
      if (await act(r.id, 'close', () => closeRequest(r.id, { outcome, note }))) setDrafts(d => without(d, r.id));
    },
  };

  // A "Take over?" that was asked for from the panel goes when the panel does:
  // coming back to the card later must not find the confirm already armed.
  // One raised by a lost race stays — it is on the row too, where it happened.
  const openPanel = useCallback((id) => {
    setSelectedId(id);
    setOffer(o => (o?.raced ? o : null));
  }, []);
  const closePanel = useCallback(() => {
    setSelectedId(null);
    setOffer(o => (o?.raced ? o : null));
  }, []);

  // The response says who is asking; whoami is the fallback if it did not.
  const me = data?.me?.email ? data.me : staff;
  const groups = groupRequests(data?.requests, me);
  const active = groups.unassigned.length + groups.mine.length + groups.others.length;

  // The open card is read from the list on every render, so a poll updates the
  // panel too. If the card has left the list (an old closed one, pushed out of
  // the most recent 50) the last copy seen stays up rather than vanishing.
  const lastSeen = useRef(null);
  const found = selectedId ? (data?.requests || []).find(r => r?.id === selectedId) : null;
  if (found) lastSeen.current = found;
  const selected = selectedId ? (found || (lastSeen.current?.id === selectedId ? lastSeen.current : null)) : null;

  // What the ~ console should understand "these requests" to mean. Nothing is
  // published until a list has loaded: zeros here would be a claim.
  useViewContext(data ? {
    view: 'request-list',
    requestsUnassigned: groups.unassigned.length,
    requestsMine: groups.mine.length,
    requestsInProgressOthers: groups.others.length,
    requestsClosedShown: groups.closed.length,
  } : null);

  const header = (
    <div className="page-header">
      <h1 className="page-title">Requests</h1>
      <p className="page-subtitle">Internal asks from the team, filed by mentioning @genius in Slack · not customer tickets</p>
    </div>
  );

  if (data === null && !loadError) return <div>{header}<Loading /></div>;

  // The load failed and there is nothing to show. This returns before any
  // list or empty state can render: "Nothing waiting" from a request that
  // never answered would send people home with work still in the queue.
  if (data === null) {
    return (
      <div>
        {header}
        <div className="error-state" role="alert">
          ⚠ {loadError} — this is a load failure, not an empty list. Retry before assuming nothing is waiting.
          <div style={{ marginTop: 12 }}>
            <button type="button" className="req-btn" onClick={load} disabled={refreshing}>{refreshing ? 'Retrying…' : 'Retry'}</button>
          </div>
        </div>
      </div>
    );
  }

  const closedTotal = Math.max(Number(data.counts?.closed) || 0, groups.closed.length);

  const cards = (list, className = 'req-cards') => (
    <ul className={className}>
      {list.map(r => (
        <RequestCard
          key={r.id}
          r={r}
          me={me}
          selected={r.id === selectedId}
          busy={busy[r.id]}
          error={actionError[r.id]}
          offer={offer?.id === r.id ? offer : null}
          actions={actions}
          onOpen={openPanel}
        />
      ))}
    </ul>
  );

  return (
    <div className="req-page">
      <div className="req-head">
        {header}
        <div className="req-toolbar">
          <span>Updated {formatTime(loadedAt)}</span>
          <button type="button" className="req-btn req-btn-quiet" onClick={load} disabled={refreshing} title="Refreshes on its own every 30 seconds">
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* A refresh that failed after a good load: the list below is real but
          old, and says so. */}
      {loadError && (
        <div className="req-stale" role="alert">
          <span>⚠ Could not refresh: {loadError}. This is the list as of {formatTime(loadedAt)}; it may be out of date.</span>
          <button type="button" className="req-btn" onClick={load} disabled={refreshing}>{refreshing ? 'Retrying…' : 'Retry'}</button>
        </div>
      )}

      {active === 0 ? (
        <div className="req-board-empty">
          <p>Nothing waiting. Mention @genius in Slack to file one.</p>
          <p className="req-board-empty-eg">@genius the Orders tab should remember my filter</p>
        </div>
      ) : (
        <div className="req-board">
          <Lane title="Unassigned" tone="open" list={groups.unassigned} renderCards={cards}
            empty="Nothing unassigned. Every open request has an owner." />
          <Lane title="Mine" tone="mine" list={groups.mine} renderCards={cards}
            empty={`Nothing on your plate.${groups.unassigned.length > 0 ? ' Pick one up from Unassigned.' : ''}`} />
          <Lane title="Others" tone="theirs" list={groups.others} renderCards={cards}
            empty="Nobody else has one open." />
        </div>
      )}

      <section className="req-shelf" aria-label="Closed">
        <button type="button" className="req-disclosure" onClick={() => setShowClosed(s => !s)} aria-expanded={showClosed}>
          <span className="req-disclosure-caret" aria-hidden="true">{showClosed ? '▾' : '▸'}</span>
          <span className="req-lane-head is-closed">
            <span>Closed</span>
            <span className="req-lane-count">{closedTotal}</span>
          </span>
          <span className="req-shelf-hint">
            {closedTotal > groups.closed.length
              ? `the ${groups.closed.length} most recent of ${closedTotal}`
              : showClosed && groups.closed.length > 0 ? 'most recent first' : null}
          </span>
        </button>
        {showClosed && (groups.closed.length > 0
          ? cards(groups.closed, 'req-cards req-cards-shelf')
          : <div className="req-lane-empty">Nothing has been closed yet.</div>)}
      </section>

      {selected && (
        <RequestDetail
          key={selected.id}
          r={selected}
          me={me}
          busy={busy[selected.id]}
          error={actionError[selected.id]}
          offer={offer?.id === selected.id ? offer : null}
          draft={drafts[selected.id] || {}}
          onDraft={patch => setDraft(selected.id, patch)}
          actions={actions}
          onClose={closePanel}
        />
      )}
    </div>
  );
}
